import { Controller } from 'stimulus';
import Choices from 'choices.js';
import '../css/vendor/_choices-js.scss';

export default class extends Controller {
  static targets = [
    'formTypeRadio',
    'systemActionSelect',
    'systemForm',
    'customForm',
    'activitySelect',
    'customActionActivitySelect',
    'limitInput',
    'unlimitedToggle'
  ]

  static values = {
    eventOnlyActions: Array,
    activityOnlyActions: Array,
    activityOptions: Array
  }

  connect() {
    this.activityChoices = null;
    this.customActionActivityChoices = null;
    this.initialSelectedActivities = Array.from(this.activitySelectTarget.selectedOptions).map(option => option.value);
    this.initializeChoicesInstances();
    this.setupInitialState();
  }

  disconnect() {
    this.destroyAllChoices();
  }

  toggleForm() {
    const selectedFormType = this.getSelectedFormType();
    this.showFormByType(selectedFormType);
  }

  handleSystemActionChange() {
    if (!this.hasSystemActionSelectTarget) return;

    const selectedAction = this.systemActionSelectTarget.value;
    this.updateActivitySection(selectedAction);
  }

  toggleUnlimited() {
    if (!this.hasUnlimitedToggleTarget || !this.hasLimitInputTarget) return;

    const isUnlimited = this.unlimitedToggleTarget.checked;
    this.limitInputTarget.disabled = isUnlimited;

    if (isUnlimited) {
      this.limitInputTarget.value = '';
    }
  }

  initializeChoicesInstances() {
    this.choicesInstances = new Map();
  }

  setupInitialState() {
    const selectedFormType = this.getSelectedFormType();
    this.showFormByType(selectedFormType);
    this.initializeAllChoices();
    this.handleSystemActionChange();
  }

  getSelectedFormType() {
    const selectedRadio = this.formTypeRadioTargets.find(radio => radio.checked);
    return selectedRadio?.value || 'system';
  }

  showFormByType(formType) {
    if (formType === 'custom') {
      this.showCustomForm();
    } else {
      this.showSystemForm();
    }
  }

  showSystemForm() {
    this.setFormVisibility('system', true);
    this.setFormVisibility('custom', false);
  }

  showCustomForm() {
    this.setFormVisibility('system', false);
    this.setFormVisibility('custom', true);
  }

  setFormVisibility(formType, isVisible) {
    const formTarget = formType === 'system' ? this.systemFormTarget : this.customFormTarget;

    if (isVisible) {
      formTarget.style.display = 'block';
      formTarget.classList.remove('form--readonly');
    } else {
      formTarget.style.display = 'none';
      formTarget.classList.add('form--readonly');
    }
  }

  updateActivitySection(selectedAction) {
    if (this.isEventOnlyAction(selectedAction)) {
      this.configureForEventOnlyAction();
    } else if (this.isActivityOnlyAction(selectedAction)) {
      this.configureForActivityOnlyAction();
    } else {
      this.enableAllActivityOptions();
    }
  }

  configureForEventOnlyAction() {
    this.clearActivitiesSelection();
    this.setActivityOptionsState(option => option !== 'event');
    this.selectEventOption();
  }

  configureForActivityOnlyAction() {
    this.clearEventSelection();
    this.setActivityOptionsState(option => option === 'event');
  }

  enableAllActivityOptions() {
    this.setActivityOptionsState(() => false);
  }

  setActivityOptionsState(disableCondition) {
    if (!this.hasActivitySelectTarget) return;

    this.updateChoicesDisabledState(disableCondition);
  }

  selectEventOption() {
    this.activityChoices.setChoiceByValue('event');
  }

  clearActivitiesSelection() {
    const allSelectedActivities = this.activityChoices.getValue(true);
    allSelectedActivities.forEach(activity => {
      this.activityChoices.removeActiveItemsByValue(activity);
    });
  }

  clearEventSelection() {
    if (!this.hasActivitySelectTarget) return;

    this.activityChoices.removeActiveItemsByValue('event');
  }

  initializeAllChoices() {
    this.destroyAllChoices();
    this.initializeSystemActionChoices();
    this.initializeActivityChoices();
  }

  initializeSystemActionChoices() {
    if (this.hasSystemActionSelectTarget) {
      this.createChoicesInstance(this.systemActionSelectTarget, {
        searchEnabled: true,
        itemSelectText: '',
        shouldSort: false,
        placeholder: true,
        placeholderValue: 'Choose an action'
      });
    }
  }

  initializeActivityChoices() {
    this.activityChoices = this.createChoicesInstance(this.activitySelectTarget, {
      removeItemButton: true,
      searchEnabled: true,
      itemSelectText: '',
      shouldSort: false,
      placeholder: true,
      placeholderValue: 'Choose activities'
    });

    this.customActionActivityChoices = this.createChoicesInstance(this.customActionActivitySelectTarget, {
      removeItemButton: true,
      searchEnabled: true,
      itemSelectText: '',
      shouldSort: false,
      placeholder: true,
      placeholderValue: 'Choose activities'
    });
  }

  createChoicesInstance(element, options) {
    try {
      const instance = new Choices(element, options);
      this.choicesInstances.set(element, instance);
      return instance;
    } catch (error) {
      // Fail silently for better user experience
      return null;
    }
  }

  updateChoicesDisabledState(disableCondition) {
    const choices = this.activityOptionsValue.map(option => ({
      label: option[0],
      value: option[1],
      disabled: disableCondition(option[1]),
      selected: this.initialSelectedActivities.includes(String(option[1]))
    }));
    this.initialSelectedActivities = [];

    this.activityChoices.setChoices(choices, 'value', 'label', true, false, true);
  }

  destroyAllChoices() {
    this.choicesInstances.forEach((instance, element) => {
      this.destroyChoicesInstance(element);
    });
    this.choicesInstances.clear();
  }

  destroyChoicesInstance(element) {
    const instance = this.choicesInstances.get(element);
    if (instance) {
      try {
        instance.destroy();
      } catch (error) {
        // Fail silently
      }
      this.choicesInstances.delete(element);
    }
  }

  isEventOnlyAction(actionType) {
    return this.eventOnlyActionsValue.includes(actionType);
  }

  isActivityOnlyAction(actionType) {
    return this.activityOnlyActionsValue.includes(actionType);
  }
}
